import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, FileText, PhoneCall, RefreshCw, Loader2, Image as ImageIcon, BarChart3, MousePointerClick } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { api } from "../../lib/api";
import Seo from "../../components/Seo";
import { Badge } from "../../components/ui/badge";

const STATUS_COLORS = {
  nouveau: "bg-terracotta-100 text-terracotta-700",
  traite: "bg-vegetal-100 text-vegetal-700",
  archive: "bg-neutral-200 text-neutral-600",
};

const TYPE_LABELS = {
  cta: "Boutons Devis / Rappel",
  call: "Clics « Appeler »",
  service: "Cartes prestations",
  form_submit: "Formulaires envoyés",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("quotes");
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoModal, setPhotoModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, q, c, a] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/quotes"),
        api.get("/admin/callbacks"),
        api.get("/admin/analytics"),
      ]);
      setStats(s.data);
      setQuotes(q.data);
      setCallbacks(c.data);
      setAnalytics(a.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("carnier_token");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("carnier_token")) { navigate("/admin/login"); return; }
    load();
  }, [load, navigate]);

  const logout = () => { localStorage.removeItem("carnier_token"); navigate("/admin/login"); };

  const setStatus = async (type, id, status) => {
    await api.patch(`/admin/${type}/${id}`, { status });
    load();
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-sand">
      <Seo title="Tableau de bord — Admin Carnier" />
      <header className="bg-vegetal-800 text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center h-9 w-9 rounded-md bg-terracotta-500 text-white font-heading font-extrabold">C</span>
            <span className="font-heading font-bold">Espace admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-vegetal-700 hover:bg-vegetal-600 transition-colors text-sm"><RefreshCw className="h-4 w-4" /> Actualiser</button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-terracotta-500 hover:bg-terracotta-600 transition-colors text-sm"><LogOut className="h-4 w-4" /> Déconnexion</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { k: "Devis reçus", v: stats?.quotes ?? "—" },
            { k: "Devis nouveaux", v: stats?.quotes_new ?? "—", accent: true },
            { k: "Rappels reçus", v: stats?.callbacks ?? "—" },
            { k: "Rappels nouveaux", v: stats?.callbacks_new ?? "—", accent: true },
          ].map((s) => (
            <div key={s.k} className="rounded-xl bg-white border border-neutral-200 p-5 shadow-sm">
              <p className={`font-heading text-3xl font-bold ${s.accent ? "text-terracotta-600" : "text-neutral-900"}`}>{s.v}</p>
              <p className="text-sm text-neutral-500 mt-1">{s.k}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-8 flex gap-2 border-b border-neutral-200">
          <button onClick={() => setTab("quotes")} data-testid="admin-tab-quotes" className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px ${tab === "quotes" ? "border-terracotta-500 text-terracotta-600" : "border-transparent text-neutral-500"}`}><FileText className="h-4 w-4" /> Demandes de devis ({quotes.length})</button>
          <button onClick={() => setTab("callbacks")} data-testid="admin-tab-callbacks" className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px ${tab === "callbacks" ? "border-terracotta-500 text-terracotta-600" : "border-transparent text-neutral-500"}`}><PhoneCall className="h-4 w-4" /> Demandes de rappel ({callbacks.length})</button>
          <button onClick={() => setTab("stats")} data-testid="admin-tab-stats" className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px ${tab === "stats" ? "border-terracotta-500 text-terracotta-600" : "border-transparent text-neutral-500"}`}><BarChart3 className="h-4 w-4" /> Statistiques & clics</button>
        </div>

        {loading ? (
          <div className="py-20 grid place-items-center text-neutral-400"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : tab === "stats" ? (
          <div className="mt-6 space-y-6" data-testid="admin-stats-panel">
            <div className="rounded-xl bg-white border border-neutral-200 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <MousePointerClick className="h-5 w-5 text-terracotta-600" />
                <h3 className="font-heading font-semibold text-neutral-900">Actions les plus cliquées</h3>
              </div>
              <p className="text-sm text-neutral-500 mb-5">Total de {analytics?.total_events ?? 0} interactions enregistrées (boutons devis, rappel, appels, cartes prestations).</p>
              {analytics?.top_labels?.length ? (
                <ResponsiveContainer width="100%" height={Math.max(180, analytics.top_labels.length * 40)}>
                  <BarChart data={analytics.top_labels.slice(0, 12)} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="label" width={220} tick={{ fontSize: 12 }} />
                    <Tooltip cursor={{ fill: "#F9F6F0" }} />
                    <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                      {analytics.top_labels.slice(0, 12).map((e, i) => (
                        <Cell key={i} fill={e.type === "cta" ? "#C05A44" : e.type === "service" ? "#4A6741" : e.type === "form_submit" ? "#853B2B" : "#7B966C"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-neutral-500 py-8 text-center text-sm">Aucune interaction enregistrée pour l'instant. Les clics des visiteurs apparaîtront ici.</p>
              )}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="rounded-xl bg-white border border-neutral-200 p-6 shadow-sm">
                <h3 className="font-heading font-semibold text-neutral-900 mb-1">Prestations les plus demandées</h3>
                <p className="text-sm text-neutral-500 mb-4">D'après les devis reçus</p>
                {analytics?.quote_types?.length ? (
                  <ul className="space-y-2.5">
                    {analytics.quote_types.map((r, i) => {
                      const max = analytics.quote_types[0]?.count || 1;
                      return (
                        <li key={i}>
                          <div className="flex justify-between text-sm mb-1"><span className="text-neutral-700">{r.label}</span><span className="font-medium text-neutral-900">{r.count}</span></div>
                          <div className="h-2 rounded-full bg-neutral-100 overflow-hidden"><div className="h-full bg-vegetal-500 rounded-full" style={{ width: `${(r.count / max) * 100}%` }} /></div>
                        </li>
                      );
                    })}
                  </ul>
                ) : <p className="text-neutral-500 py-6 text-center text-sm">Aucun devis reçu pour l'instant.</p>}
              </div>

              <div className="rounded-xl bg-white border border-neutral-200 p-6 shadow-sm">
                <h3 className="font-heading font-semibold text-neutral-900 mb-1">Répartition des interactions</h3>
                <p className="text-sm text-neutral-500 mb-4">Par type d'action</p>
                {analytics?.by_type?.length ? (
                  <ul className="space-y-3">
                    {analytics.by_type.map((r) => (
                      <li key={r.type} className="flex items-center justify-between rounded-lg bg-sand-100 border border-neutral-100 px-4 py-3">
                        <span className="text-sm text-neutral-700">{TYPE_LABELS[r.type] || r.type}</span>
                        <span className="font-heading font-bold text-neutral-900">{r.count}</span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-neutral-500 py-6 text-center text-sm">Aucune donnée.</p>}
              </div>
            </div>
          </div>
        ) : tab === "quotes" ? (
          <div className="mt-6 space-y-4" data-testid="admin-quotes-list">
            {quotes.length === 0 && <p className="text-neutral-500 py-10 text-center">Aucune demande de devis pour l'instant.</p>}
            {quotes.map((q) => (
              <div key={q.id} className="rounded-xl bg-white border border-neutral-200 p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-semibold text-neutral-900">{q.nom}</h3>
                      <Badge className={STATUS_COLORS[q.status] || STATUS_COLORS.nouveau}>{q.status}</Badge>
                      {q.rappel && <Badge className="bg-vegetal-100 text-vegetal-700">Souhaite un rappel</Badge>}
                    </div>
                    <p className="text-sm text-neutral-500 mt-1">{fmtDate(q.created_at)}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setStatus("quotes", q.id, "traite")} className="text-xs px-2.5 py-1.5 rounded-md bg-vegetal-50 text-vegetal-700 hover:bg-vegetal-100">Marquer traité</button>
                    <button onClick={() => setStatus("quotes", q.id, "archive")} className="text-xs px-2.5 py-1.5 rounded-md bg-neutral-100 text-neutral-600 hover:bg-neutral-200">Archiver</button>
                  </div>
                </div>
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <Info label="Téléphone" value={<a href={`tel:${q.telephone}`} className="text-terracotta-600">{q.telephone}</a>} />
                  <Info label="Email" value={<a href={`mailto:${q.email}`} className="text-terracotta-600 break-all">{q.email}</a>} />
                  <Info label="Code postal" value={q.code_postal} />
                  <Info label="Type de demande" value={q.type_demande} />
                  {q.type_bien && <Info label="Type de bien" value={q.type_bien} />}
                  {q.delai && <Info label="Délai" value={q.delai} />}
                </div>
                <div className="mt-3 rounded-lg bg-sand-100 border border-neutral-100 p-3 text-sm text-neutral-700">{q.message}</div>
                {q.photo && (
                  <button onClick={() => setPhotoModal(q.photo)} className="mt-3 inline-flex items-center gap-2 text-sm text-terracotta-600 font-medium"><ImageIcon className="h-4 w-4" /> Voir la photo jointe</button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-6 space-y-4" data-testid="admin-callbacks-list">
            {callbacks.length === 0 && <p className="text-neutral-500 py-10 text-center">Aucune demande de rappel pour l'instant.</p>}
            {callbacks.map((c) => (
              <div key={c.id} className="rounded-xl bg-white border border-neutral-200 p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading font-semibold text-neutral-900">{c.nom}</h3>
                      <Badge className={STATUS_COLORS[c.status] || STATUS_COLORS.nouveau}>{c.status}</Badge>
                    </div>
                    <p className="text-sm text-neutral-500 mt-1">{fmtDate(c.created_at)}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setStatus("callbacks", c.id, "traite")} className="text-xs px-2.5 py-1.5 rounded-md bg-vegetal-50 text-vegetal-700 hover:bg-vegetal-100">Marquer traité</button>
                    <button onClick={() => setStatus("callbacks", c.id, "archive")} className="text-xs px-2.5 py-1.5 rounded-md bg-neutral-100 text-neutral-600 hover:bg-neutral-200">Archiver</button>
                  </div>
                </div>
                <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
                  <Info label="Téléphone" value={<a href={`tel:${c.telephone}`} className="text-terracotta-600">{c.telephone}</a>} />
                  {c.creneau && <Info label="Créneau" value={c.creneau} />}
                  {c.sujet && <Info label="Sujet" value={c.sujet} />}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {photoModal && (
        <div onClick={() => setPhotoModal(null)} className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-6 cursor-zoom-out">
          <img src={photoModal} alt="Photo du chantier" className="max-h-[85vh] max-w-full rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs text-neutral-400 uppercase tracking-wide">{label}</p>
      <p className="text-neutral-800 font-medium">{value}</p>
    </div>
  );
}
